import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Phone, Mail, MapPin, User, Moon, Sun } from 'lucide-react';
import { FaCcStripe, FaPaypal, FaApplePay } from 'react-icons/fa';
import { useCart } from '@/context/CartContext';
import { useAuth, authHeaders } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Dark mode hook (persists in localStorage, admin-only) ----
const DARK_KEY = 'boss_dark_mode';
const useDarkMode = () => {
  const [dark, setDark] = useState(() => localStorage.getItem(DARK_KEY) === 'true');
  useEffect(() => {
    if (dark) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
    localStorage.setItem(DARK_KEY, dark ? 'true' : 'false');
  }, [dark]);
  return [dark, () => setDark(d => !d)];
};

const useUnreadCount = (user) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!user) { setCount(0); return; }
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API}/orders/unread-count`, { headers: authHeaders() });
        if (res.ok) { const d = await res.json(); setCount(d.unread_count || 0); }
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [user]);
  return count;
};

const TopBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const location = useLocation();
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0];
  const unreadCount = useUnreadCount(user);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY < 30);
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';
  const isDark = scrolled || !isHome;
  const topBarBg = isDark ? 'bg-white' : 'bg-transparent';
  const topBarText = isDark ? 'text-[#5a5a5a]' : 'text-white';
  const linkColor = isDark ? 'text-[#3a3a3a] hover:text-teal-600' : 'text-white hover:text-gray-300';
  const rightLinkColor = isDark ? 'hover:text-teal-600' : 'hover:text-gray-300';

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-[60] hidden md:block transition-[background-color,opacity,transform] duration-300 ${topBarBg} ${visible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}
      style={{ borderBottom: isHome ? (isDark ? '1px solid #f7f7f7' : '1px solid rgba(247, 247, 247, 0.1)') : '1px solid #F5F2EE' }}
      data-testid="top-bar"
    >
      <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between py-[15px] text-[12px] tracking-wide">
        <span className={`${topBarText} transition-colors duration-300`}>
          Part of the{' '}
          <a 
            href="https://bosssofas.com.au" 
            target="_blank" 
            rel="noopener noreferrer" 
            className={`font-medium ${linkColor} transition-colors duration-300`}
            data-testid="boss-sofas-link"
          >
            Boss Sofas &amp; Reupholstery
          </a>
          {' '}family
        </span>
        <div className="flex items-center gap-6">
          <a href="#" className={`${topBarText} ${rightLinkColor} transition-colors duration-300`} data-testid="about-us-link">About us</a>
          <a href="#" className={`${topBarText} ${rightLinkColor} transition-colors duration-300`} data-testid="contact-us-link">Contact us</a>
          <span className={`${topBarText} opacity-40`}>|</span>
          <Link to="/account" className={`${topBarText} ${rightLinkColor} transition-colors duration-300 inline-flex items-center gap-1.5`} data-testid="my-account-link">
            {user ? (<><User className="w-3.5 h-3.5" />{firstName}{unreadCount > 0 && <span className="flex items-center gap-0.5 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full"><Mail className="w-2.5 h-2.5" />{unreadCount}</span>}</>) : 'My account'}
          </Link>
          {!user && <Link to="/track-order" className={`${topBarText} ${rightLinkColor} transition-colors duration-300`} data-testid="track-order-link">Track my order</Link>}
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const { cart } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const firstName = user?.name?.split(' ')[0];
  const unreadCount = useUnreadCount(user);
  const [dark, toggleDark] = useDarkMode();

  const [topBarVisible, setTopBarVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setTopBarVisible(window.scrollY < 30);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';
  const isDark = scrolled || !isHome;
  const headerBg = isDark ? 'bg-white' : 'bg-transparent';
  const headerBorder = isHome ? (isDark ? '1px solid #f7f7f7' : 'none') : '1px solid #F5F2EE';
  const textColor = isDark ? 'text-[#1A1A1A]' : 'text-white';
  const showTealLogo = isDark;

  const navLinks = [
    { to: '/products/indoor-seat-cushions', label: 'Indoor Cushions' },
    { to: '/products/outdoor-seat-cushions', label: 'Outdoor Cushions' },
    { to: '/products/sofa-cushions', label: 'Sofa Cushions' },
    { to: '/fabric-samples', label: 'Fabric Samples' },
    { to: '/our-foams', label: 'Our Foams' },
    { to: '/faqs', label: 'FAQs' },
    { to: '/trade', label: 'Trade' },
  ];

  return (
    <header 
      className={`fixed left-0 right-0 z-50 transition-[background-color] duration-300 ${headerBg} ${topBarVisible ? 'md:top-[42px] top-0' : 'top-0'}`}
      style={{ borderBottom: headerBorder }}
      data-testid="main-header"
    >
      <div className={`max-w-[1632px] mx-auto transition-all duration-500 ${scrolled ? 'px-3 sm:px-8 lg:px-12' : 'px-5 sm:px-8 lg:px-12'}`}>
        <div className={`flex items-center justify-between transition-all duration-500 ${scrolled ? 'h-11 sm:h-16' : 'h-20'}`}>
          <Link 
            to="/" 
            className="flex items-center transition-all duration-500 relative"
            data-testid="logo-link"
          >
            <img 
              src="/logo-white.png" 
              alt="Boss Cushions" 
              className={`transition-all duration-700 object-contain ${scrolled ? 'h-6 sm:h-7 lg:h-7 xl:h-9' : 'h-8 sm:h-8 lg:h-7 xl:h-10'} ${showTealLogo ? 'opacity-0' : 'opacity-100'}`}
              data-testid="header-logo-white"
            />
            <img 
              src="/logo-teal.png" 
              alt="Boss Cushions" 
              className={`absolute left-0 top-1/2 -translate-y-1/2 transition-all duration-700 object-contain ${scrolled ? 'h-6 sm:h-7 lg:h-7 xl:h-9' : 'h-8 sm:h-8 lg:h-7 xl:h-10'} ${showTealLogo ? 'opacity-100' : 'opacity-0'}`}
              data-testid="header-logo-teal"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map(link => {
              // Active when current path matches the link OR shares its category root.
              const isActive =
                location.pathname === link.to ||
                (link.to.startsWith('/products/') && location.pathname.startsWith(link.to));
              // On the homepage hero (transparent header) the nav text is white and hover stays grey.
              // On any white-background header (scrolled or non-home), hover/active turns teal.
              const navTextClass = isDark
                ? isActive
                  ? 'text-teal-600'
                  : 'text-[#1A1A1A] hover:text-teal-600'
                : 'text-white hover:text-gray-300';
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`${navTextClass} font-medium text-[14px] tracking-wide transition-colors duration-300`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/products"
              className="hidden lg:inline-flex bg-teal-600 hover:bg-teal-700 text-white font-semibold text-[13px] px-4 py-1.5 rounded-full transition-colors duration-300"
              data-testid="nav-build-now"
            >
              Build Now
            </Link>
            <Link
              to="/cart"
              className={`relative p-2.5 ${textColor} ${isDark ? 'hover:text-teal-600' : 'hover:text-gray-300'} transition-colors duration-300`}
              data-testid="cart-link"
            >
              <ShoppingCart className="h-5 w-5" />
              {cart.item_count > 0 && (
                <span 
                  className="absolute top-0 -right-1 bg-teal-500 text-white text-[10px] font-bold rounded-full h-[18px] w-[18px] flex items-center justify-center"
                  data-testid="cart-count"
                >
                  {cart.item_count}
                </span>
              )}
            </Link>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className={`${textColor} !bg-transparent focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 hover:!bg-transparent active:!bg-transparent hover:!text-inherit active:!text-inherit`} data-testid="mobile-menu-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-cream overflow-y-auto" data-testid="mobile-menu">
                <nav className="flex flex-col gap-4 pb-8">
                  <img src="/logo-teal.png" alt="Boss Cushions" className="h-8 object-contain object-left mb-2" data-testid="mobile-menu-logo" />
                  {navLinks.map(link => {
                    const isActive =
                      location.pathname === link.to ||
                      (link.to.startsWith('/products/') && location.pathname.startsWith(link.to));
                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`text-lg font-display font-medium transition-colors ${
                          isActive ? 'text-teal-600' : 'text-[#1A1A1A] hover:text-teal-600'
                        }`}
                        data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                  <div className="border-t border-gray-200 pt-4 mt-1 flex flex-col gap-4">
                    <Link to="/account" onClick={() => setMobileMenuOpen(false)} className="text-base font-body text-gray-500 hover:text-teal-600 transition-colors flex items-center gap-1.5">
                      <User className="w-4 h-4" />
                      {user ? firstName : 'My account'}
                      {user && unreadCount > 0 && <span className="flex items-center gap-0.5 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full"><Mail className="w-2.5 h-2.5" />{unreadCount}</span>}
                    </Link>
                    {!user && <Link to="/track-order" onClick={() => setMobileMenuOpen(false)} className="text-base font-body text-gray-500 hover:text-teal-600 transition-colors">Track my order</Link>}
                    <a href="#" onClick={() => setMobileMenuOpen(false)} className="text-base font-body text-gray-500 hover:text-teal-600 transition-colors">About us</a>
                    <a href="#" onClick={() => setMobileMenuOpen(false)} className="text-base font-body text-gray-500 hover:text-teal-600 transition-colors">Contact us</a>
                    {user?.is_admin && (
                      <button
                        onClick={toggleDark}
                        className="flex items-center gap-2 text-base font-body text-gray-500 hover:text-teal-600 transition-colors"
                        data-testid="dark-mode-toggle"
                      >
                        {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        {dark ? 'Light Mode' : 'Dark Mode'}
                      </button>
                    )}
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

const Footer = () => {
  return (
    <footer
      className="bg-teal-500 text-white"
      data-testid="main-footer"
    >
      <div className="max-w-[1664px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 pb-10">
        <div className="flex flex-col md:flex-row justify-between items-start gap-14 mb-14">
          <div className="md:max-w-xs">
            <img src="/logo-white.png" alt="Boss Cushions" className="h-10 object-contain block mb-4" data-testid="footer-logo" />
            <p className="text-white/70 text-[15px] leading-relaxed mb-6 font-body">
              Custom made cushions crafted to your exact specifications.
            </p>
            <div className="flex gap-4">
              <FaCcStripe className="h-8 w-8 text-white/40 hover:text-white/70 transition-colors" />
              <FaPaypal className="h-8 w-8 text-white/40 hover:text-white/70 transition-colors" />
              <FaApplePay className="h-8 w-8 text-white/40 hover:text-white/70 transition-colors" />
            </div>
          </div>

          <div className="flex flex-wrap gap-14 sm:gap-20">
            <div>
              <h4 className="font-body font-semibold text-xs uppercase tracking-[0.08em] mb-5 text-white/60">Shop</h4>
              <ul className="space-y-3">
                <li><Link to="/products/indoor-seat-cushions" className="text-white/80 hover:text-white transition-colors text-[15px] font-body">Indoor Cushions</Link></li>
                <li><Link to="/products/outdoor-seat-cushions" className="text-white/80 hover:text-white transition-colors text-[15px] font-body">Outdoor Cushions</Link></li>
                <li><Link to="/products" className="text-white/80 hover:text-white transition-colors text-[15px] font-body">All Products</Link></li>
                <li><Link to="/fabric-samples" className="text-white/80 hover:text-white transition-colors text-[15px] font-body">Fabric Samples</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-body font-semibold text-xs uppercase tracking-[0.08em] mb-5 text-white/60">Info</h4>
              <ul className="space-y-3">
                <li><span className="text-white/80 text-[15px] font-body">About Us</span></li>
                <li><Link to="/our-foams" className="text-white/80 hover:text-white transition-colors text-[15px] font-body">Our Foams</Link></li>
                <li><Link to="/faqs" className="text-white/80 hover:text-white transition-colors text-[15px] font-body">FAQs</Link></li>
                <li><Link to="/trade" className="text-white/80 hover:text-white transition-colors text-[15px] font-body">Trade</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-body font-semibold text-xs uppercase tracking-[0.08em] mb-5 text-white/60">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2.5 text-white/80 text-[15px] font-body">
                  <Phone className="h-4 w-4 text-teal-300" />
                  <span>1-800-CUSHION</span>
                </li>
                <li className="flex items-center gap-2.5 text-white/80 text-[15px] font-body">
                  <Mail className="h-4 w-4 text-teal-300" />
                  <span>hello@bosscushions.com</span>
                </li>
                <li className="flex items-start gap-2.5 text-white/80 text-[15px] font-body">
                  <MapPin className="h-4 w-4 mt-0.5 text-teal-300" />
                  <span>Melbourne, VIC</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tagline */}
        <p className="font-display text-3xl sm:text-4xl text-white/25 tracking-tight italic mb-8">
          Comfort, crafted daily.
        </p>

        <div className="border-t border-white/15 pt-6 text-center text-white/40 text-sm font-body">
          <p>&copy; {new Date().getFullYear()} Boss Cushions. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();
  const footerRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(0);
  // Reveal effect is desktop + homepage only. Every other page renders a normal scrolling footer.
  const isHome = location.pathname === '/';
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true
  );
  const useReveal = isHome && isDesktop;

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Track footer height so main content gets a matching bottom margin (creates scroll room so the fixed footer reveals as you scroll).
  useEffect(() => {
    if (!useReveal) {
      setFooterHeight(0);
      return;
    }
    const el = footerRef.current;
    if (!el) return;
    const update = () => setFooterHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [useReveal]);

  return (
    <div className="overflow-x-hidden">
      <TopBar />
      <Header />
      {/* Reveal mode (homepage desktop): main sits on top of a fixed footer with a matching bottom margin so the footer reveals as you scroll. Everywhere else: normal flow, no fixed footer. */}
      <main
        className={`overflow-x-hidden ${useReveal ? 'relative z-10 min-h-screen bg-white' : ''}`}
        style={useReveal ? { marginBottom: `${footerHeight}px` } : undefined}
        data-testid="main-content"
      >
        {children}
      </main>
      <div
        ref={footerRef}
        className={useReveal ? 'fixed bottom-0 left-0 right-0 w-full z-0' : ''}
        data-testid="footer-reveal-wrapper"
      >
        <Footer />
      </div>
    </div>
  );
};

export default Layout;
